import {useCallback, useMemo} from 'react';

import {AsyncStatusesEnum} from '../../../libs/shared/helpers/src/types/asyncStatuses';
import {CancelToken} from '../../helpers/asyncCancelHelpers';
import {useAppSelector} from '../../helpers/reduxHelpers';
import {CompanyModel} from '../../models/api/companyModel';
import {
  ApiCustomFields,
  CustomFieldModel,
  CustomFieldResourceTypesEnum,
} from '../../models/api/customFieldModel';
import {SettingsCustomFieldsPagesEnum} from '../../models/url/settingsUrlModel';
import {fetchApiAsync} from '../../services/http/http';
import {getCustomFieldsForCardsFlat} from '../../store/data/customFields/customFieldsSelectors';
import {SettingsBannersEnum, useSettingsBanners} from '../banners/settingsBannersContext';
import {useSettingsQuery} from '../helpers/useSettingsQuery';
import {parseApiError} from './customFieldsHelpers';

/*
 * Consts.
 */

const CustomFieldsKey = 'customFields';

/*
 * Types.
 */

interface UseCustomFieldsReturnType {
  data?: ReadonlyArray<CustomFieldModel>;
  status: AsyncStatusesEnum;
  error?: any;
}

/*
 * Hooks.
 */

export function useSettingsContactCustomFields(): UseCustomFieldsReturnType {
  const contactCustomFields = useAppSelector(getCustomFieldsForCardsFlat);

  return {data: contactCustomFields, status: AsyncStatusesEnum.LOADED};
}

export function useSettingsTeammateCustomFields(): UseCustomFieldsReturnType {
  return useFetchCustomFields(SettingsCustomFieldsPagesEnum.TEAMMATES);
}

export function useSettingsInboxCustomFields(): UseCustomFieldsReturnType {
  return useFetchCustomFields(SettingsCustomFieldsPagesEnum.INBOXES);
}

export function useSettingsAccountCustomFields(): UseCustomFieldsReturnType {
  return useFetchCustomFields(SettingsCustomFieldsPagesEnum.ACCOUNTS);
}

function fieldForResource(type: CustomFieldResourceTypesEnum) {
  return (field: CustomFieldModel) => field.resourceType === type;
}

export function useSettingsCustomFields() {
  const {data, status, error} = useFetchCustomFields();

  return useMemo(() => {
    const customFields: Record<CustomFieldResourceTypesEnum, Array<CustomFieldModel>> = {
      [CustomFieldResourceTypesEnum.CARD]:
        data?.filter(fieldForResource(CustomFieldResourceTypesEnum.CARD)) ?? [],
      [CustomFieldResourceTypesEnum.ACCOUNT]:
        data?.filter(fieldForResource(CustomFieldResourceTypesEnum.ACCOUNT)) ?? [],
      [CustomFieldResourceTypesEnum.TEAMMATE]:
        data?.filter(fieldForResource(CustomFieldResourceTypesEnum.TEAMMATE)) ?? [],
      [CustomFieldResourceTypesEnum.INBOX]:
        data?.filter(fieldForResource(CustomFieldResourceTypesEnum.INBOX)) ?? [],
    };

    return {
      ...customFields,
      status,
      error,
    };
  }, [data, status, error]);
}

function useCustomFieldErrorBanner() {
  const {showBanner} = useSettingsBanners();

  return useCallback(
    (err: any) => {
      showBanner({message: parseApiError(err), type: SettingsBannersEnum.ERROR});
    },
    [showBanner],
  );
}

function useFetchCustomFields(resource?: SettingsCustomFieldsPagesEnum) {
  const onError = useCustomFieldErrorBanner();

  const query = useSettingsQuery(
    [CustomFieldsKey, {resource: resource ?? 'all'}],
    ({company, cancelToken}) => baseFetchCustomFields(company, resource, cancelToken),
    {onError},
  );

  return {data: query.data, status: query.asyncStatus, error: query.error};
}

/*
 * Helpers.
 */

async function baseFetchCustomFields(
  company: CompanyModel,
  resource: SettingsCustomFieldsPagesEnum | undefined,
  cancelToken: CancelToken,
) {
  const url = resource ? `${company.url}/${resource}/custom_fields` : `${company.url}/all_custom_fields`;
  return fetchApiAsync(ApiCustomFields, url, {cancelToken});
}
